import React, { useEffect, useMemo, useState } from 'react';
import { productsAPI } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../common/Toast';
import type { Product } from '../../types/product';
import { formatPrice } from '../../utils/currency';
import { useContrastInk } from '../../utils/useContrastInk';

interface FBTItem {
  id: number;
  name: string;
  slug: string;
  priceInt: number;
  currency: string;
  imageUrl: string | null;
}

interface FrequentlyBoughtTogetherProps {
  product: Product;
  className?: string;
}

// Use the shared formatter, not a local one: it renders MYR as "RM 320.00"
// like the rest of the storefront, and only falls back to an ISO-code label for
// currencies Intl can't name. A local `${currency} ${amount}` printed
// "MYR 320.00" right next to the price block's "RM 320.00" on the same page.
const fmtPrice = (sen: number, currency: string) =>
  formatPrice(sen, true, currency || 'MYR');

const productPriceInt = (p: Product): number => {
  if (typeof (p as any).priceInt === 'number') return (p as any).priceInt;
  if (typeof p.price === 'number') return Math.round(p.price * 100);
  return 0;
};

const productImage = (p: Product): string | null => {
  if (Array.isArray(p.images) && p.images.length > 0) return p.images[0];
  if (typeof p.imageUrl === 'string') return p.imageUrl;
  if (Array.isArray(p.imageUrl) && p.imageUrl.length > 0) return p.imageUrl[0];
  return null;
};

/**
 * Frequently Bought Together — main product + 2-3 related items.
 * All items selected by default. Total updates as user toggles.
 * One-click batch add to cart.
 */
export const FrequentlyBoughtTogether: React.FC<FrequentlyBoughtTogetherProps> = ({
  product,
  className = '',
}) => {
  const { addItem } = useCart();
  const { showToast } = useToast();
  const [items, setItems] = useState<FBTItem[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  /**
   * The panel used to paint `var(--card-bg, #ffffff)` while its text used
   * `var(--card-text, #111827)`. Dark templates set `--card-text` (cream, for
   * cards on a dark surface) but leave `--card-bg` unset, so the panel took the
   * hardcoded white fallback and rendered cream-on-white — the heading and every
   * product name were invisible on Atelier.
   *
   * Pairing them by hand per template does not scale, so the panel now takes the
   * page surface when the tenant has no card colour, and the ink is measured off
   * whatever the panel actually renders. Same approach as the section CTAs.
   */
  // State, not a plain ref: a ref assignment doesn't re-render, so the hook
  // would keep measuring `null` and never leave the fallback. Same pattern the
  // section CTAs use.
  const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null);
  const ink = useContrastInk(panelEl, '#111827');
  const inkMuted = `color-mix(in srgb, ${ink} 65%, transparent)`;
  const inkFaint = `color-mix(in srgb, ${ink} 12%, transparent)`;

  // Always include the main product in the bundle.
  const mainSelected = selected.has(product.id);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await productsAPI.getFBT(product.id);
        if (!alive) return;
        const fetched = (res?.items || []).slice(0, 3);
        setItems(fetched);
        // Default-select main + all related (per spec).
        const init = new Set<number>([product.id]);
        for (const it of fetched) init.add(it.id);
        setSelected(init);
      } catch {
        // Silent — don't surface SEO/empty result to shoppers.
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [product.id]);

  const currency = items[0]?.currency || (product as any).currency || 'MYR';
  const mainPrice = productPriceInt(product);

  const totalSen = useMemo(() => {
    let sum = 0;
    if (mainSelected) sum += mainPrice;
    for (const it of items) {
      if (selected.has(it.id)) sum += it.priceInt;
    }
    return sum;
  }, [items, selected, mainSelected, mainPrice]);

  const fullSen = useMemo(() => {
    let sum = mainPrice;
    for (const it of items) sum += it.priceInt;
    return sum;
  }, [items, mainPrice]);

  // No discount system here — the row stays visible with combined price.
  // If a backend discount comes in later, this is the spot to subtract.
  const savingsSen = 0;

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddSelected = async () => {
    if (adding) return;
    const picks: Array<{ id: number; name: string }> = [];

    // Main product comes from upstream PDP cart action — only add it if user
    // has the main checkbox ticked AND it's not already in cart through
    // the page-level Add-to-cart button. To keep this simple and atomic,
    // we always add what's selected here; idempotency comes from cart merge.
    if (mainSelected) picks.push({ id: product.id, name: product.name });
    for (const it of items) {
      if (selected.has(it.id)) picks.push({ id: it.id, name: it.name });
    }

    if (picks.length === 0) {
      showToast?.('Select at least one item to add to cart.', 'warning');
      return;
    }

    setAdding(true);
    try {
      // Main product — add directly (we already have the full Product).
      if (mainSelected) {
        addItem(product, 1);
      }
      // Related items — synthesize a minimal Product-shaped object for cart.
      for (const it of items) {
        if (!selected.has(it.id)) continue;
        const synthetic = {
          id: it.id,
          name: it.name,
          slug: it.slug,
          description: '',
          price: it.priceInt / 100,
          priceInt: it.priceInt,
          currency: it.currency,
          productType: (product as any).productType,
          tenantId: (product as any).tenantId,
          status: 'active',
          active: true,
          images: it.imageUrl ? [it.imageUrl] : undefined,
          imageUrl: it.imageUrl || undefined,
        } as unknown as Product;
        addItem(synthetic, 1);
      }
      showToast?.(
        picks.length === 1
          ? '1 item added to cart'
          : `${picks.length} items added to cart`,
        'success',
      );
    } catch (err: any) {
      showToast?.(err?.message || 'Failed to add bundle to cart', 'error');
    } finally {
      setAdding(false);
    }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  // Render item row (image + checkbox + name + price).
  const renderRow = (
    img: string | null,
    name: string,
    priceInt: number,
    isMain: boolean,
    id: number,
    isSelected: boolean,
  ) => (
    <label
      key={id}
      className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl border transition-colors cursor-pointer"
      style={{
        borderColor: isSelected
          ? 'var(--primary-color, #0F766E)'
          : inkFaint,
        background: isSelected
          ? 'color-mix(in srgb, var(--primary-color, #0F766E) 5%, transparent)'
          : 'transparent',
      }}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => toggle(id)}
        className="w-5 h-5 rounded accent-[var(--primary-color,#0F766E)] flex-shrink-0 cursor-pointer"
        aria-label={`Include ${name} in bundle`}
      />
      <div
        className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden flex-shrink-0"
        style={{ background: '#f3f4f6' }}
      >
        {img ? (
          <img
            src={img}
            alt={name}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : null}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-sm sm:text-[15px] font-medium truncate"
          style={{ color: ink }}
        >
          {name}
          {isMain && (
            <span
              className="ml-2 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
              style={{
                background: inkFaint,
                color: inkMuted,
              }}
            >
              This item
            </span>
          )}
        </div>
        <div
          className="text-xs sm:text-sm mt-0.5"
          style={{ color: inkMuted }}
        >
          {fmtPrice(priceInt, currency)}
        </div>
      </div>
    </label>
  );

  return (
    <section
      className={`max-w-3xl mx-auto w-full ${className}`}
      aria-labelledby="fbt-heading"
    >
      <div
        ref={setPanelEl}
        className="rounded-3xl p-5 sm:p-6 border"
        style={{
          borderColor: inkFaint,
          // Falls back to the page surface, not a hardcoded white, so a dark
          // template gets a dark panel instead of a white slab.
          background: 'var(--card-bg, var(--color-background, #ffffff))',
        }}
      >
        <h2
          id="fbt-heading"
          className="font-bold mb-2"
          style={{
            color: ink,
            fontSize: '1.125rem',
            lineHeight: 1.3,
          }}
        >
          Frequently Bought Together
        </h2>
        <p
          className="text-xs sm:text-sm mb-4"
          style={{ color: inkMuted }}
        >
          Customers who bought this also added these.
        </p>

        <div className="space-y-2">
          {renderRow(
            productImage(product),
            product.name,
            mainPrice,
            true,
            product.id,
            mainSelected,
          )}
          {items.map((it) =>
            renderRow(it.imageUrl, it.name, it.priceInt, false, it.id, selected.has(it.id)),
          )}
        </div>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div
              className="text-xs uppercase tracking-wider"
              style={{ color: inkMuted }}
            >
              Bundle total
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span
                className="text-xl sm:text-2xl font-bold"
                style={{ color: ink }}
              >
                {fmtPrice(totalSen, currency)}
              </span>
              {savingsSen > 0 && (
                <>
                  <span
                    className="text-sm line-through"
                    style={{ color: inkMuted }}
                  >
                    {fmtPrice(fullSen, currency)}
                  </span>
                  <span
                    className="text-xs sm:text-sm font-semibold px-2 py-0.5 rounded-full"
                    style={{
                      background:
                        'color-mix(in srgb, var(--primary-color, #0F766E) 12%, transparent)',
                      color: 'var(--primary-color, #0F766E)',
                    }}
                  >
                    Save {fmtPrice(savingsSen, currency)}
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={adding || totalSen === 0}
            onClick={handleAddSelected}
            className="rounded-full px-6 py-3 font-semibold text-white transition-transform hover:scale-[0.99] active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--primary-color,#0F766E)]"
            style={{ background: 'var(--primary-color, #0F766E)' }}
          >
            {adding ? 'Adding…' : 'Add selected to cart'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default FrequentlyBoughtTogether;
